import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, X } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const POLL_MS = 20000;

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState(null);
  const popoverRef = useRef(null);

  const refresh = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [user?.id]);

  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      await refresh();
    } catch (e) { setError(formatErr(e)); }
  };

  const markOne = async (id) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      await refresh();
    } catch (e) { setError(formatErr(e)); }
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative h-9 w-9 grid place-items-center rounded-full border hairline hover:border-teal/40 hover:text-teal transition"
        data-testid="notifications-bell"
        aria-label={t("notifications.heading", "Notifications")}
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center text-[10px] font-bold rounded-full bg-rose-500 text-white"
                data-testid="notifications-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-w-[92vw] glass-solid rounded-2xl shadow-ink overflow-hidden z-50" data-testid="notifications-popover">
          <div className="flex items-center justify-between px-4 py-3 border-b hairline">
            <div className="font-serif-display text-lg">{t("notifications.heading", "Notifications")}</div>
            <button onClick={markAll}
                    className="text-[11px] text-muted hover:text-teal transition flex items-center gap-1"
                    data-testid="notifications-mark-all">
              <Check size={12}/> {t("notifications.mark_all_read", "Mark all read")}
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {error && <div className="text-rose-300 text-xs px-4 py-2">{error}</div>}
            {items.length === 0 ? (
              <div className="p-8 text-center text-muted text-sm">
                {t("notifications.empty", "No notifications yet.")}
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <li key={n.id}
                      className={cx("group px-4 py-3 border-b hairline last:border-0 hover:bg-white/[0.03] transition",
                                    !n.is_read && "bg-teal/[0.04]")}
                      data-testid={`notif-${n.id}`}>
                    <div className="flex items-start gap-3">
                      {!n.is_read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-teal shrink-0"/>}
                      <div className="flex-1 min-w-0">
                        {n.link ? (
                          <Link to={n.link} onClick={() => { setOpen(false); markOne(n.id); }} className="block">
                            <div className="text-sm leading-tight">{n.title}</div>
                            {n.body && <div className="text-xs text-muted mt-0.5 line-clamp-2">{n.body}</div>}
                          </Link>
                        ) : (
                          <>
                            <div className="text-sm leading-tight">{n.title}</div>
                            {n.body && <div className="text-xs text-muted mt-0.5 line-clamp-2">{n.body}</div>}
                          </>
                        )}
                        <div className="text-[10px] text-muted/60 font-mono mt-1">{timeAgo(n.created_at)}</div>
                      </div>
                      {!n.is_read && (
                        <button onClick={() => markOne(n.id)}
                                className="opacity-0 group-hover:opacity-100 text-muted hover:text-teal transition"
                                aria-label="Mark read"
                                data-testid={`notif-read-${n.id}`}>
                          <X size={12}/>
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
